import React from "react";
import {
  Stack,
  Typography,
  IconButton,
  Badge,
  Grid,
  Box,
} from "@mui/material";
import {
  VideoLibrary as VideoIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  FitnessCenter as FitnessCenterIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import moment from "moment";


import {
  StyledVideosCard,
  VideosHeader,
  VideosContent,
} from "./StyledComponents";

const VideoStatisticsCard = ({
  allVideos = [],
  unprocessedVideos = [],
  processedVideos = [],
  exerciseVideos = [],
  generalVideos = [],
  mostRecentVideo = null,
  onRefreshThumbnails = () => {},
  onRefreshVideos = () => {},
  loadingThumbnails = false,
  isRefreshing = false,
}) => {
  // Handle the refresh button click - refresh both videos and thumbnails
  const handleRefresh = () => {
    // First refresh the videos from the server
    onRefreshVideos();

    // Then refresh the thumbnails
    onRefreshThumbnails();
  };

  return (
    <StyledVideosCard>
      <VideosHeader>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <VideoIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            Video Library
          </Typography>
        </Stack>
        <IconButton
          size="small"
          color="primary"
          disabled={loadingThumbnails || isRefreshing}
          onClick={handleRefresh}
          title="Refresh videos and thumbnails"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </VideosHeader>
      <VideosContent>
        {/* Responsive layout - stacked on mobile, horizontal on desktop */}
        <Grid container spacing={2}>
          {/* Main statistics section - full width */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "center", sm: "flex-start" },
                gap: 3,
              }}
            >
              {/* Pending Videos Count */}
              <Box
                sx={{
                  textAlign: "center",
                  minWidth: { sm: 120 },
                  flex: { sm: "0 0 auto" },
                }}
              >
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  color={
                    unprocessedVideos.length > 0 ? "warning.main" : "primary.main"
                  }
                >
                  {unprocessedVideos.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {unprocessedVideos.length === 1 ? "Video" : "Videos"} Pending
                  Review
                </Typography>
                {allVideos.length > unprocessedVideos.length && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {allVideos.length - unprocessedVideos.length} processed
                  </Typography>
                )}
              </Box>

              {/* Stats Grid - Responsive layout */}
              <Box sx={{ flex: 1, width: "100%" }}>
                <Grid container spacing={2}>
                  {/* Review Stats */}
                  <Grid item xs={6} sm={3}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <Badge color="warning" variant="dot">
                        <VideoIcon color="primary" fontSize="small" />
                      </Badge>
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        Waiting
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color={
                        unprocessedVideos.length > 0
                          ? "warning.main"
                          : "text.primary"
                      }
                    >
                      {unprocessedVideos.length}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <CheckIcon color="success" fontSize="small" />
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        Processed
                      </Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight="bold">
                      {processedVideos.length}
                    </Typography>
                  </Grid>

                  {/* Video Type Stats */}
                  <Grid item xs={6} sm={3}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <FitnessCenterIcon color="primary" fontSize="small" />
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        Exercise
                      </Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight="bold">
                      {exerciseVideos.length}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <VideoIcon color="secondary" fontSize="small" />
                      <Typography variant="body2" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}>
                        General
                      </Typography>
                    </Stack>
                    <Typography variant="body1" fontWeight="bold">
                      {generalVideos.length}
                    </Typography>
                  </Grid>

                  {/* Most Recent - Full width on mobile */}
                  <Grid item xs={12} sm={12}>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      justifyContent={{ xs: "center", sm: "flex-start" }}
                      spacing={1}
                      sx={{ pt: 1 }}
                    >
                      <ScheduleIcon color="primary" fontSize="small" />
                      <Typography variant="body2">Most Recent:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {mostRecentVideo
                          ? moment(mostRecentVideo.createdOn.toDate()).fromNow()
                          : "N/A"}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </VideosContent>
    </StyledVideosCard>
  );
};

export default VideoStatisticsCard;
